// src/admin/pages/ProductDetailPage.tsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Typography,
  Paper,
  Stack,
  TextField,
  Checkbox,
  FormControlLabel,
  Alert,
  ImageList,
  ImageListItem,
  IconButton,
  Divider,
  Chip,
  MenuItem,
  Dialog,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation } from "@apollo/client";
import {
  ADMIN_PRODUCTS_QUERY,
  CREATE_PRODUCT_MUTATION,
  UPDATE_PRODUCT_MUTATION,
} from "../../graphql/products";
import AdminLoading from "../Components/AdminLoading";
import {
  uploadProductImages,
  buildProductImageUrl,
} from "../../api/productImages";
import {
  ADMIN_PRODUCT_FAQS_QUERY,
  ADMIN_UPSERT_PRODUCT_FAQ_MUTATION,
  ADMIN_DELETE_PRODUCT_FAQ_MUTATION,
} from "../../graphql/faqs";
import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import QuizIcon from "@mui/icons-material/Quiz";
import {
  PRODUCT_INTRO_QUERY,
  ADMIN_UPSERT_PAGE_INTRO_MUTATION,
  ADMIN_DELETE_PAGE_INTRO_MUTATION,
} from "../../graphql/pageIntros";
import RichTextEditor from "../../components/RichTextEditor";

type ProductForm = {
  id: string;
  name: string;
  productType: string;
  altText?: string | null;
  customSchema?: string | null;
  metal: string;
  shortDescription?: string | null;
  longDescription?: string | null;
  price: number;
  bestSeller: boolean;
  rotationImages: string[];
  galleryImages: string[];
};

const emptyProduct: ProductForm = {
  id: "",
  name: "",
  productType: "",
  metal: "",
  shortDescription: "",
  longDescription: "",
  price: 0,
  altText: "",
  customSchema: "",
  bestSeller: false,
  rotationImages: [],
  galleryImages: [],
};

const ProductDetailPage: React.FC = () => {
  const { productId } = useParams<{ productId: string }>();
  const isNew = !productId || productId === "new";
  const navigate = useNavigate();
  const [faqForm, setFaqForm] = useState<{
    id?: string;
    question: string;
    answer: string;
    position: number;
    active: boolean;
  }>({ question: "", answer: "", position: 0, active: true });

  const [openFaqForm, setOpenFaqForm] = useState(false);
  const [confirmDeleteFaq, setConfirmDeleteFaq] = useState<any>(null);
  const [introContent, setIntroContent] = useState("");
  const [introId, setIntroId] = useState<string | null>(null);
  const [openIntroForm, setOpenIntroForm] = useState(false);

  const { data, loading, error } = useQuery(ADMIN_PRODUCTS_QUERY, {
    skip: isNew,
  });

  const [createProduct, { loading: creating }] = useMutation(
    CREATE_PRODUCT_MUTATION,
    { refetchQueries: [{ query: ADMIN_PRODUCTS_QUERY }] },
  );
  const [updateProduct, { loading: updating }] = useMutation(
    UPDATE_PRODUCT_MUTATION,
    { refetchQueries: [{ query: ADMIN_PRODUCTS_QUERY }] },
  );
  const { data: faqData, refetch: refetchFaqs } = useQuery(
    ADMIN_PRODUCT_FAQS_QUERY,
    {
      variables: { productId: productId },
      fetchPolicy: "cache-and-network",
      skip: isNew || !productId,
    },
  );

  const [upsertFaq, upsertFaqState] = useMutation(
    ADMIN_UPSERT_PRODUCT_FAQ_MUTATION,
  );
  const [deleteFaqMutation, deleteFaqState] = useMutation(
    ADMIN_DELETE_PRODUCT_FAQ_MUTATION,
  );

  const { data: introData, refetch: refetchIntro } = useQuery(
    PRODUCT_INTRO_QUERY,
    {
      variables: { productId },
      fetchPolicy: "cache-and-network",
      skip: isNew || !productId,
      onCompleted: (d) => {
        if (d?.productIntro) {
          setIntroContent(d.productIntro.content);
          setIntroId(d.productIntro.id);
        }
      },
    },
  );

  const [upsertIntro, upsertIntroState] = useMutation(
    ADMIN_UPSERT_PAGE_INTRO_MUTATION,
  );
  const [deleteIntroMutation] = useMutation(ADMIN_DELETE_PAGE_INTRO_MUTATION);

  const faqs = faqData?.adminProductFaqs ?? [];

  const [form, setForm] = useState<ProductForm>(emptyProduct);
  const [rotationFiles, setRotationFiles] = useState<File[]>([]);
  const [galleryFiles, setGalleryFiles] = useState<File[]>([]);
  const [actionError, setActionError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const products: ProductForm[] = useMemo(
    () => (data?.products as ProductForm[]) || [],
    [data],
  );

  useEffect(() => {
    if (isNew) {
      setForm(emptyProduct);
      return;
    }

    const found = products.find((p) => p.id === productId);
    if (found) {
      setForm({
        ...emptyProduct,
        ...found,
      });
    }
  }, [isNew, products, productId]);

  const type = (form.productType || "").trim().toLowerCase();
  const canShowRotation = type === "ring" || type === "wedding band";

  useEffect(() => {
    if (!canShowRotation && rotationFiles.length > 0) {
      setRotationFiles([]);
    }
  }, [canShowRotation]);

  const busy = loading || creating || updating || saving;

  const handleChange = (key: keyof ProductForm, value: any) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleRotationFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;
    const arr = Array.from(e.target.files);
    setRotationFiles((prev) => [...prev, ...arr]);
    e.target.value = "";
  };

  const handleGalleryFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;
    const arr = Array.from(e.target.files);
    setGalleryFiles((prev) => [...prev, ...arr]);
    e.target.value = "";
  };

  const handleDeleteRotationImage = (index: number) => {
    setForm((prev) => ({
      ...prev,
      rotationImages: prev.rotationImages.filter((_, i) => i !== index),
    }));
  };

  const handleDeleteGalleryImage = (index: number) => {
    setForm((prev) => ({
      ...prev,
      galleryImages: prev.galleryImages.filter((_, i) => i !== index),
    }));
  };

  const handleRemoveAllRotation = () => {
    setForm((prev) => ({ ...prev, rotationImages: [] }));
  };

  const handleRemoveAllGallery = () => {
    setForm((prev) => ({ ...prev, galleryImages: [] }));
  };

  const handleSave = async () => {
    if (!form.name.trim() || !form.productType.trim()) {
      setActionError("Name and Type are required.");
      return;
    }

    setSaving(true);
    setActionError(null);

    try {
      const [uploadedRotation, uploadedGallery] = await Promise.all([
        canShowRotation
          ? uploadProductImages(rotationFiles, "rotation")
          : Promise.resolve([] as string[]),
        uploadProductImages(galleryFiles, "gallery"),
      ]);

      const vars = {
        id: form.id,
        name: form.name.trim(),
        productType: form.productType.trim(),
        metal: form.metal.trim(),
        shortDescription: form.shortDescription || "",
        longDescription: form.longDescription || "",
        price: form.price,
        altText: form.altText || "",
        customSchema: form.customSchema || "",
        bestSeller: form.bestSeller,

        rotationImages: canShowRotation
          ? [...form.rotationImages, ...uploadedRotation]
          : form.rotationImages,
        galleryImages: [...form.galleryImages, ...uploadedGallery],
      };

      if (isNew) {
        const { id, ...rest } = vars;
        await createProduct({ variables: rest });
      } else {
        await updateProduct({ variables: vars });
      }

      navigate("/admin/products");
    } catch (err: any) {
      setActionError(err.message || "Failed to save product");
    } finally {
      setSaving(false);
      setRotationFiles([]);
      setGalleryFiles([]);
    }
  };

  const handleSaveIntro = async () => {
    if (!productId) return;
    const res = await upsertIntro({
      variables: {
        id: introId || null,
        pageType: "product",
        productId,
        content: introContent,
        active: true,
      },
    });
    const errors = res?.data?.adminUpsertPageIntro?.errors ?? [];
    if (errors.length) return;
    setOpenIntroForm(false);
    await refetchIntro();
  };

  const handleDeleteIntro = async () => {
    if (!introId) return;
    await deleteIntroMutation({ variables: { id: introId } });
    setIntroContent("");
    setIntroId(null);
    await refetchIntro();
  };

  const handleSaveFaq = async () => {
    if (!productId) return;
    const res = await upsertFaq({
      variables: {
        id: faqForm.id || null,
        productId,
        question: faqForm.question.trim(),
        answer: faqForm.answer.trim(),
        position: faqForm.position,
        active: faqForm.active,
      },
    });
    const errors = res?.data?.adminUpsertProductFaq?.errors ?? [];
    if (errors.length) return;
    setOpenFaqForm(false);
    setFaqForm({ question: "", answer: "", position: 0, active: true });
    await refetchFaqs();
  };

  const handleDeleteFaq = async () => {
    if (!confirmDeleteFaq) return;
    await deleteFaqMutation({ variables: { id: confirmDeleteFaq.id } });
    setConfirmDeleteFaq(null);
    await refetchFaqs();
  };

  const handleCancel = () => {
    navigate("/admin/products");
  };

  return (
    <>
      <AdminLoading open={busy} />

      <Box
        sx={{
          maxWidth: 1200,
          mx: "auto",
          pb: 4,
        }}
      >
        {/* Top bar */}
        <Paper
          elevation={1}
          sx={{
            p: 2,
            mb: 3,
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            alignItems: { xs: "flex-start", md: "center" },
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Box>
            <Typography variant="overline" color="text.secondary">
              {isNew ? "Create New Product" : "Edit Product"}
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 600 }}>
              {form.name || "Untitled Product"}
            </Typography>
            <Stack direction="row" spacing={1} mt={1}>
              {form.productType && (
                <Chip
                  size="small"
                  label={form.productType}
                  color="default"
                  variant="outlined"
                />
              )}
              <Chip
                size="small"
                label={`Price: ${form.price || 0}`}
                variant="outlined"
              />

              {/* Best Seller chip for ANY product type */}
              {form.bestSeller && (
                <Chip size="small" label="Best Seller" color="success" />
              )}
            </Stack>
          </Box>

          <Stack direction="row" spacing={1}>
            <Button variant="outlined" onClick={handleCancel}>
              Back to Products
            </Button>
            <Button variant="contained" onClick={handleSave} disabled={saving}>
              {saving ? "Saving..." : "Save Product"}
            </Button>
          </Stack>
        </Paper>

        {error && !isNew && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Failed to load product: {error.message}
          </Alert>
        )}
        {actionError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {actionError}
          </Alert>
        )}

        {/* Main layout: details left, images right on desktop */}
        <Stack
          direction={{ xs: "column", lg: "row" }}
          spacing={3}
          alignItems="flex-start"
        >
          {/* Product details */}
          <Paper
            elevation={1}
            sx={{
              flex: 1.1,
              p: 2.5,
            }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }} mb={1}>
              Product Details
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Manage core information for this product. These details will be
              used across the store.
            </Typography>

            <Stack spacing={2}>
              <TextField
                label="Name"
                value={form.name}
                onChange={(e) => handleChange("name", e.target.value)}
                fullWidth
              />

              {/* TYPE */}
              <TextField
                select
                label="Type"
                value={form.productType}
                onChange={(e) => handleChange("productType", e.target.value)}
                fullWidth
              >
                <MenuItem value="">Select type</MenuItem>
                <MenuItem value="ring">ring</MenuItem>
                <MenuItem value="wedding band">wedding band</MenuItem>
                <MenuItem value="bridal sets">bridal sets</MenuItem>
                <MenuItem value="necklace">necklace</MenuItem>
                <MenuItem value="ear rings">ear rings</MenuItem>
                <MenuItem value="bracelets">bracelets</MenuItem>
                <MenuItem value="karas">karas</MenuItem>
              </TextField>

              <TextField
                select
                label="Metal"
                value={form.metal}
                onChange={(e) => handleChange("metal", e.target.value)}
                fullWidth
              >
                <MenuItem value="">Select metal</MenuItem>
                <MenuItem value="gold">gold</MenuItem>
                <MenuItem value="diamond">diamond</MenuItem>
                <MenuItem value="diamond polki">diamond polki</MenuItem>
                <MenuItem value="platinum">platinum</MenuItem>
                <MenuItem value="palladium">palladium</MenuItem>
              </TextField>

              <TextField
                label="Short Description"
                multiline
                minRows={2}
                value={form.shortDescription || ""}
                onChange={(e) =>
                  handleChange("shortDescription", e.target.value)
                }
                fullWidth
              />
              <TextField
                label="Long Description"
                multiline
                minRows={4}
                value={form.longDescription || ""}
                onChange={(e) =>
                  handleChange("longDescription", e.target.value)
                }
                fullWidth
              />

              <TextField
                label="Image Alt Text"
                value={form.altText || ""}
                onChange={(e) => handleChange("altText", e.target.value)}
                fullWidth
                placeholder="e.g. 18K Gold Diamond Solitaire Ring - Haroon Sharif Jewellers"
                helperText="This text describes the product images for search engines and accessibility. Be descriptive and include keywords."
              />

              <TextField
                label="JSON-LD Schema Markup"
                value={form.customSchema || ""}
                onChange={(e) => handleChange("customSchema", e.target.value)}
                fullWidth
                multiline
                minRows={6}
                placeholder={`{\n  "@context": "https://schema.org",\n  "@type": "Product",\n  "name": "Product Name"\n}`}
                helperText="Paste your JSON-LD schema here. This will be injected into the page <head> for SEO. Must be valid JSON."
                sx={{
                  "& .MuiInputBase-input": {
                    fontFamily: "monospace",
                    fontSize: "0.8rem",
                  },
                }}
              />

              <Divider sx={{ my: 1 }} />

              <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                <TextField
                  label="Price"
                  type="number"
                  value={form.price}
                  onChange={(e) =>
                    handleChange("price", parseFloat(e.target.value || "0"))
                  }
                  fullWidth
                />

                {/* Best Seller available for ALL product types */}
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={form.bestSeller}
                      onChange={(e) =>
                        handleChange("bestSeller", e.target.checked)
                      }
                    />
                  }
                  label="Best Seller"
                />
              </Stack>
            </Stack>
          </Paper>

          {/* Images column */}
          <Stack flex={1.3} spacing={3} width="100%">
            {/* Rotation Images ONLY for ring or wedding band */}
            {canShowRotation ? (
              <Paper elevation={1} sx={{ p: 2.5 }}>
                <Stack
                  direction={{ xs: "column", md: "row" }}
                  justifyContent="space-between"
                  alignItems={{ xs: "flex-start", md: "center" }}
                  mb={2}
                  spacing={2}
                >
                  <Box>
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: 600 }}
                      gutterBottom
                    >
                      Rotation Images ({form.rotationImages.length})
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Upload images used for 360° rotation views of the product.
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={1}>
                    <Button
                      variant="outlined"
                      component="label"
                      startIcon={<AddPhotoAlternateIcon />}
                    >
                      Add Rotation Images
                      <input
                        type="file"
                        hidden
                        multiple
                        accept="image/*"
                        onChange={handleRotationFileChange}
                      />
                    </Button>
                    <Button
                      variant="text"
                      color="error"
                      onClick={handleRemoveAllRotation}
                      disabled={form.rotationImages.length === 0}
                    >
                      Remove All
                    </Button>
                  </Stack>
                </Stack>

                {rotationFiles.length > 0 && (
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    {rotationFiles.length} new file
                    {rotationFiles.length > 1 ? "s" : ""} selected (will be
                    uploaded on save).
                  </Typography>
                )}

                {form.rotationImages.length === 0 ? (
                  <Box
                    sx={{
                      borderRadius: 1,
                      border: "1px dashed rgba(0,0,0,0.2)",
                      p: 3,
                      textAlign: "center",
                      color: "text.secondary",
                      fontSize: 14,
                    }}
                  >
                    No rotation images yet. Use{" "}
                    <strong>“Add Rotation Images”</strong> to upload.
                  </Box>
                ) : (
                  <ImageList cols={3} gap={8} sx={{ maxHeight: "353px" }}>
                    {form.rotationImages.map((src, idx) => (
                      <ImageListItem key={src + idx}>
                        <Box
                          sx={{
                            position: "relative",
                            borderRadius: 1,
                            overflow: "hidden",
                            border: "1px solid rgba(0,0,0,0.1)",
                            bgcolor: "#fafafa",
                          }}
                        >
                          <img
                            src={buildProductImageUrl(src)}
                            alt={`Rotation ${idx + 1}`}
                            style={{
                              width: "100%",
                              height: 160,
                              objectFit: "cover",
                              display: "block",
                            }}
                          />
                          <IconButton
                            size="small"
                            sx={{
                              position: "absolute",
                              top: 4,
                              right: 4,
                              bgcolor: "rgba(0,0,0,0.6)",
                              color: "#fff",
                              "&:hover": { bgcolor: "rgba(0,0,0,0.8)" },
                            }}
                            onClick={() => handleDeleteRotationImage(idx)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </ImageListItem>
                    ))}
                  </ImageList>
                )}
              </Paper>
            ) : null}

            {/* Gallery Images */}
            <Paper elevation={1} sx={{ p: 2.5 }}>
              <Stack
                direction={{ xs: "column", md: "row" }}
                justifyContent="space-between"
                alignItems={{ xs: "flex-start", md: "center" }}
                mb={2}
                spacing={2}
              >
                <Box>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: 600 }}
                    gutterBottom
                  >
                    Gallery Images ({form.galleryImages.length})
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    These images appear in the main gallery on the product page.
                  </Typography>
                </Box>
                <Stack direction="row" spacing={1}>
                  <Button
                    variant="outlined"
                    component="label"
                    startIcon={<AddPhotoAlternateIcon />}
                  >
                    Add Gallery Images
                    <input
                      type="file"
                      hidden
                      multiple
                      accept="image/*"
                      onChange={handleGalleryFileChange}
                    />
                  </Button>
                  <Button
                    variant="text"
                    color="error"
                    onClick={handleRemoveAllGallery}
                    disabled={form.galleryImages.length === 0}
                  >
                    Remove All
                  </Button>
                </Stack>
              </Stack>

              {galleryFiles.length > 0 && (
                <Typography variant="body2" color="text.secondary" mb={1}>
                  {galleryFiles.length} new file
                  {galleryFiles.length > 1 ? "s" : ""} selected (will be
                  uploaded on save).
                </Typography>
              )}

              {form.galleryImages.length === 0 ? (
                <Box
                  sx={{
                    borderRadius: 1,
                    border: "1px dashed rgba(0,0,0,0.2)",
                    p: 3,
                    textAlign: "center",
                    color: "text.secondary",
                    fontSize: 14,
                  }}
                >
                  No gallery images yet. Use{" "}
                  <strong>“Add Gallery Images”</strong> to upload.
                </Box>
              ) : (
                <ImageList cols={3} gap={8} sx={{ maxHeight: "353px" }}>
                  {form.galleryImages.map((src, idx) => (
                    <ImageListItem key={src + idx}>
                      <Box
                        sx={{
                          position: "relative",
                          borderRadius: 1,
                          overflow: "hidden",
                          border: "1px solid rgba(0,0,0,0.1)",
                          bgcolor: "#fafafa",
                        }}
                      >
                        <img
                          src={buildProductImageUrl(src)}
                          alt={`Gallery ${idx + 1}`}
                          style={{
                            width: "100%",
                            height: 160,
                            objectFit: "cover",
                            display: "block",
                          }}
                        />
                        <IconButton
                          size="small"
                          sx={{
                            position: "absolute",
                            top: 4,
                            right: 4,
                            bgcolor: "rgba(0,0,0,0.6)",
                            color: "#fff",
                            "&:hover": { bgcolor: "rgba(0,0,0,0.8)" },
                          }}
                          onClick={() => handleDeleteGalleryImage(idx)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </ImageListItem>
                  ))}
                </ImageList>
              )}
            </Paper>
          </Stack>
        </Stack>
        {/* FAQ SECTION - only show when editing existing product */}
        {!isNew && productId && (
          <Paper elevation={1} sx={{ p: 2.5, mt: 3 }}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              mb={2}
            >
              <Box>
                <Stack direction="row" spacing={1} alignItems="center">
                  <QuizIcon sx={{ fontSize: 20, color: "text.secondary" }} />
                  <Typography variant="subtitle1" fontWeight={600}>
                    Product FAQs ({faqs.length})
                  </Typography>
                </Stack>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 0.5 }}
                >
                  These appear on this product's page below "You May Also Like".
                  Max 3 FAQs recommended.
                </Typography>
              </Box>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => {
                  setFaqForm({
                    question: "",
                    answer: "",
                    position: faqs.length,
                    active: true,
                  });
                  setOpenFaqForm(true);
                }}
                size="small"
              >
                Add FAQ
              </Button>
            </Stack>

            {faqs.length === 0 ? (
              <Box
                sx={{
                  border: "1px dashed rgba(0,0,0,0.2)",
                  borderRadius: 1,
                  p: 3,
                  textAlign: "center",
                  color: "text.secondary",
                  fontSize: 14,
                }}
              >
                No FAQs yet. Click "Add FAQ" to add questions about this
                product.
              </Box>
            ) : (
              <Stack spacing={1.5}>
                {faqs.map((faq: any) => (
                  <Box
                    key={faq.id}
                    sx={{
                      border: "1px solid rgba(0,0,0,0.08)",
                      borderRadius: 1,
                      p: 2,
                      bgcolor: "#fafafa",
                    }}
                  >
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="flex-start"
                    >
                      <Box sx={{ flex: 1, pr: 1 }}>
                        <Typography
                          sx={{ fontWeight: 600, fontSize: 13, mb: 0.5 }}
                        >
                          Q: {faq.question}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: 13,
                            color: "text.secondary",
                            lineHeight: 1.6,
                          }}
                        >
                          A: {faq.answer}
                        </Typography>
                        <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                          <Chip
                            size="small"
                            label={`Order: ${faq.position}`}
                            variant="outlined"
                            sx={{ borderRadius: 1, fontSize: 11 }}
                          />
                          <Chip
                            size="small"
                            label={faq.active ? "Active" : "Hidden"}
                            color={faq.active ? "success" : "default"}
                            variant="outlined"
                            sx={{ borderRadius: 1, fontSize: 11 }}
                          />
                        </Stack>
                      </Box>
                      <Stack direction="row" spacing={0.5}>
                        <IconButton
                          size="small"
                          onClick={() => {
                            setFaqForm({
                              id: faq.id,
                              question: faq.question,
                              answer: faq.answer,
                              position: faq.position,
                              active: faq.active,
                            });
                            setOpenFaqForm(true);
                          }}
                        >
                          <EditOutlinedIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => setConfirmDeleteFaq(faq)}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                    </Stack>
                  </Box>
                ))}
              </Stack>
            )}
          </Paper>
        )}

        {!isNew && productId && (
          <Paper elevation={1} sx={{ p: 2.5, mt: 3 }}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              mb={2}
            >
              <Box>
                <Typography variant="subtitle1" fontWeight={600}>
                  Product Page Intro Content
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 0.5 }}
                >
                  Descriptive text that appears below "You May Also Like" on
                  this product's page.
                </Typography>
              </Box>
              <Stack direction="row" spacing={1}>
                {introId && (
                  <Button
                    variant="outlined"
                    color="error"
                    size="small"
                    onClick={handleDeleteIntro}
                  >
                    Remove
                  </Button>
                )}
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setOpenIntroForm(true)}
                >
                  {introId ? "Edit Intro" : "Add Intro"}
                </Button>
              </Stack>
            </Stack>

            {introContent ? (
              <Box
                sx={{
                  border: "1px solid rgba(0,0,0,0.08)",
                  borderRadius: 1,
                  p: 2,
                  bgcolor: "#fafafa",
                  fontSize: 13,
                  color: "text.secondary",
                  maxHeight: 120,
                  overflow: "hidden",
                  "& p": { mb: 1 },
                }}
                dangerouslySetInnerHTML={{ __html: introContent }}
              />
            ) : (
              <Box
                sx={{
                  border: "1px dashed rgba(0,0,0,0.2)",
                  borderRadius: 1,
                  p: 3,
                  textAlign: "center",
                  color: "text.secondary",
                  fontSize: 14,
                }}
              >
                No intro content yet. Click "Add Intro" to add descriptive text
                for this product.
              </Box>
            )}

            {/* Intro Form Dialog */}
            <Dialog
              open={openIntroForm}
              onClose={() => setOpenIntroForm(false)}
              maxWidth="md"
              fullWidth
            >
              <Box sx={{ p: 3 }}>
                <Typography variant="h6" fontWeight={700}>
                  {introId ? "Edit Product Intro" : "Add Product Intro"}
                </Typography>
                <Divider sx={{ my: 2 }} />
                <RichTextEditor
                  value={introContent}
                  onChange={setIntroContent}
                  placeholder="Write descriptive content for this product page..."
                />
                <Divider sx={{ my: 2 }} />
                <Stack direction="row" justifyContent="flex-end" spacing={1}>
                  <Button
                    onClick={() => setOpenIntroForm(false)}
                    disabled={upsertIntroState.loading}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    onClick={handleSaveIntro}
                    disabled={upsertIntroState.loading || !introContent.trim()}
                  >
                    {upsertIntroState.loading ? "Saving..." : "Save"}
                  </Button>
                </Stack>
              </Box>
            </Dialog>
          </Paper>
        )}

        {/* FAQ FORM DIALOG */}
        <Dialog
          open={openFaqForm}
          onClose={() => setOpenFaqForm(false)}
          maxWidth="sm"
          fullWidth
        >
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight={700}>
              {faqForm.id ? "Edit FAQ" : "Add FAQ"}
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Stack spacing={2}>
              <TextField
                label="Question"
                value={faqForm.question}
                onChange={(e) =>
                  setFaqForm((p) => ({ ...p, question: e.target.value }))
                }
                fullWidth
                placeholder="e.g. What metal is this ring made of?"
              />
              <TextField
                label="Answer"
                value={faqForm.answer}
                onChange={(e) =>
                  setFaqForm((p) => ({ ...p, answer: e.target.value }))
                }
                fullWidth
                multiline
                minRows={4}
              />
              <Stack direction="row" spacing={2}>
                <TextField
                  label="Display Order"
                  type="number"
                  value={faqForm.position}
                  onChange={(e) =>
                    setFaqForm((p) => ({
                      ...p,
                      position: Number(e.target.value),
                    }))
                  }
                  fullWidth
                  helperText="Lower = shown first"
                />
                <TextField
                  select
                  label="Status"
                  value={faqForm.active ? "active" : "hidden"}
                  onChange={(e) =>
                    setFaqForm((p) => ({
                      ...p,
                      active: e.target.value === "active",
                    }))
                  }
                  fullWidth
                >
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="hidden">Hidden</MenuItem>
                </TextField>
              </Stack>
            </Stack>
            <Divider sx={{ my: 2 }} />
            <Stack direction="row" justifyContent="flex-end" spacing={1}>
              <Button
                onClick={() => setOpenFaqForm(false)}
                disabled={upsertFaqState.loading}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSaveFaq}
                disabled={
                  upsertFaqState.loading ||
                  !faqForm.question.trim() ||
                  !faqForm.answer.trim()
                }
              >
                {upsertFaqState.loading ? "Saving..." : "Save FAQ"}
              </Button>
            </Stack>
          </Box>
        </Dialog>

        {/* FAQ DELETE CONFIRM */}
        <Dialog
          open={!!confirmDeleteFaq}
          onClose={() => setConfirmDeleteFaq(null)}
          maxWidth="xs"
          fullWidth
        >
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight={700}>
              Delete FAQ?
            </Typography>
            <Typography color="text.secondary" sx={{ mt: 1 }}>
              Delete: <b>"{confirmDeleteFaq?.question}"</b>
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Stack direction="row" justifyContent="flex-end" spacing={1}>
              <Button onClick={() => setConfirmDeleteFaq(null)}>Cancel</Button>
              <Button
                variant="contained"
                color="error"
                onClick={handleDeleteFaq}
                disabled={deleteFaqState.loading}
              >
                {deleteFaqState.loading ? "Deleting..." : "Delete"}
              </Button>
            </Stack>
          </Box>
        </Dialog>
      </Box>
    </>
  );
};

export default ProductDetailPage;
